import { Image } from "#/api/fileStore.type";
import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft } from "../icons/chevron-left";
import { ChevronRight } from "../icons/chevron-right";
import { cn, getMimeTypeFromExtension } from "../utls/utils";
import { ImagePreview } from "./image-preview";

interface ImageCarouselProps {
  size: "small" | "large";
  images: Image[];
  onRemove?: (index: number) => void;
}

export function ImageCarousel({
  size = "small",
  images,
  onRemove,
}: ImageCarouselProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isScrollable, setIsScrollable] = useState(false);
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;

    if (scrollContainer) {
      const hasScroll =
        scrollContainer.scrollWidth > scrollContainer.clientWidth;
      setIsScrollable(hasScroll);
    }
  }, [images]);

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const scrollContainer = event.currentTarget;
    setIsAtStart(scrollContainer.scrollLeft === 0);
    setIsAtEnd(
      Math.ceil(scrollContainer.scrollLeft + scrollContainer.clientWidth) >=
        scrollContainer.scrollWidth
    );

    // Calculate active index based on scroll position
    if (scrollContainer.children.length) {
      const itemWidth =
        scrollContainer.children[0].getBoundingClientRect().width;
      const scrollLeft = scrollContainer.scrollLeft;
      const newIndex = Math.round(
        scrollLeft / (itemWidth + (size === "small" ? 8 : 16))
      );

      if (
        newIndex !== activeIndex &&
        newIndex >= 0 &&
        newIndex < images.length
      ) {
        setActiveIndex(newIndex);
      }
    }
  };

  const scrollToImage = (index: number) => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer && scrollContainer.children[index]) {
      const child = scrollContainer.children[index] as HTMLElement;
      const containerWidth = scrollContainer.clientWidth;
      const childWidth = child.offsetWidth;
      const childLeft = child.offsetLeft;
      const scrollLeft = childLeft - containerWidth / 2 + childWidth / 2;

      scrollContainer.scrollTo({
        left: scrollLeft,
        behavior: "smooth",
      });

      setActiveIndex(index);
    }
  };

  const handlePrevious = () => {
    if (activeIndex > 0) {
      scrollToImage(activeIndex - 1);
    }
  };

  const handleNext = () => {
    if (activeIndex < images.length - 1) {
      scrollToImage(activeIndex + 1);
    }
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        scrollContainerRef.current === document.activeElement ||
        scrollContainerRef.current?.contains(document.activeElement as Node)
      ) {
        if (e.key === "ArrowLeft") {
          e.preventDefault();
          handlePrevious();
        } else if (e.key === "ArrowRight") {
          e.preventDefault();
          handleNext();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [activeIndex, images.length]);

  return (
    <div data-testid="image-carousel" className="relative">
      {isScrollable && (
        <button
          onClick={handlePrevious}
          disabled={isAtStart}
          className={cn(
            "absolute right-full transform top-1/2 -translate-y-1/2 z-10",
            "focus:outline-none focus:ring-2 focus:ring-terracotta-500 rounded-full"
          )}
          aria-label="Previous image"
          tabIndex={0}
        >
          <ChevronLeft active={!isAtStart} />
        </button>
      )}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className={cn(
          "flex overflow-x-auto hide-scrollbar",
          size === "small" && "gap-2",
          size === "large" && "gap-4"
        )}
        tabIndex={0}
        role="region"
        aria-label="Image carousel"
      >
        {images.map((src, index) => (
          <ImagePreview
            key={index}
            size={size}
            src={src.url}
            onRemove={onRemove && (() => onRemove(index))}
            fileType={getMimeTypeFromExtension(src.fileName)}
            fileName={src.fileName}
            isActive={index === activeIndex}
            onClick={() => scrollToImage(index)}
            allImages={images}
            currentIndex={index}
          />
        ))}
      </div>
      {isScrollable && (
        <button
          onClick={handleNext}
          disabled={isAtEnd}
          className={cn(
            "absolute left-full transform top-1/2 -translate-y-1/2 z-10",
            "focus:outline-none focus:ring-2 focus:ring-terracotta-500 rounded-full"
          )}
          aria-label="Next image"
          tabIndex={0}
        >
          <ChevronRight active={!isAtEnd} />
        </button>
      )}

      {/* Pagination dots for multiple images */}
      {images.length > 1 && (
        <div className="flex justify-center mt-2 gap-1">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => scrollToImage(index)}
              className={cn(
                "w-1.5 h-1.5 rounded-full transition-all",
                index === activeIndex
                  ? "bg-terracotta-500 w-3"
                  : "bg-terracotta-300 hover:bg-terracotta-400"
              )}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
